import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { parseSystemSettings, DEFAULT_SETTINGS, type SystemSettings } from '@/lib/system-settings';

export type { SystemSettings };

const SYSTEM_SETTINGS_KEY = ['system-settings'];

export function useSystemSettings() {
  const queryClient = useQueryClient();

  const { data: settings = DEFAULT_SETTINGS, isLoading, refetch } = useQuery({
    queryKey: SYSTEM_SETTINGS_KEY,
    // System settings change very rarely — cache aggressively, dedupe across all mounts.
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('system_settings')
        .select('key, value')
        .in('key', [
          'late_tolerance_minutes',
          'allowed_checkin_radius',
          'payment_grace_days',
          'payment_grace_reminder_hours',
          'payment_grace_timezone',
          'app_min_version_android',
          'app_min_version_ios',
          'app_recommended_version_android',
          'app_recommended_version_ios',
          'app_store_url_android',
          'app_store_url_ios',
        ]);

      if (error) {
        console.error('Error fetching system settings:', error);
        return DEFAULT_SETTINGS;
      }

      return parseSystemSettings(data || []);
    },
  });

  return {
    settings,
    isLoading,
    refetch: () => queryClient.invalidateQueries({ queryKey: SYSTEM_SETTINGS_KEY }).then(() => refetch()),
  };
}
